import React, { useEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MobileStepper,
  Modal,
  Typography
} from '@mui/material';
import logoImage from '../assets/cmcos_logo_white.png';
import pulldownImage from '../assets/navi-pulldownImg.svg';
import stepImg from '../assets/navi-step.svg';
import co2SttingImg from '../assets/navi-co2Setting.svg';
import {
  gray100,
  gray70,
  gray90,
  primary100,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor,
  white
} from '../../constants/Colors';
import { useNavigate } from 'react-router-dom';
import { userRoleEnum } from '../../constants/Enum';

interface Props {
  show: boolean;
  userRole: number | null;
  onClose: () => void;
}

const styles = {
  modaleWrapper: {
    background: 'white',
    maxWidth: '560px',
    minWidth: '340px',
    maxHeight: '90vh',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column'
  },
  text: {
    color: gray100,
    fontSize: '16px'
  }
};

const Navipanel: React.FC<Props> = (props): JSX.Element => {
  const { show, userRole, onClose } = props;

  const navigate = useNavigate();

  const stepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <Typography sx={styles.text}>
              CMCOSは、各建材メーカーから販売されている低CO2建材を検索・比較できるシステムです。
              <br />
              現状、CO2算定のルールが統一されていないため、各建材メーカーのCO2原単位がバラバラで算定されており、そのまま大小を比較するのが困難でした。
              そこでCMCOSでは以下の手順により、異なるルールで算定されたCO2原単位でも比較・ソートできるようにしています。
            </Typography>
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={stepImg}
                style={{
                  margin: '24px auto 0',
                  maxWidth: '400px',
                  width: '100%'
                }}
              />
            </Box>
          </>
        );
      case 1:
        return (
          <>
            <Typography sx={styles.text}>
              具体的には、下図のように「％による換算値」「メーカー算定値」の２種類のCO2原単位が表示できます。
            </Typography>
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={co2SttingImg}
                style={{
                  margin: '24px auto 0',
                  maxWidth: '400px',
                  width: '100%'
                }}
              />
            </Box>
          </>
        );
      case 2:
        return (
          <>
            <Typography sx={styles.text}>
              通常は換算値でランキング表示しますが、メーカー算定値のまま利用したい場合は、プルダウンメニューで「メーカー算定値で表示」を選択していただくと、メーカー算定値のままで比較することができます。
            </Typography>
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={pulldownImage}
                style={{ margin: '24px auto', width: '220px' }}
              />
            </Box>
            <Typography sx={styles.text}>
              なお、CMCOS上の情報は、各建材メーカー自身により入力されています。公開時に簡易的なチェックはCMCOS上で行いますが、詳細内容の検証はしていないため、掲載内容のご利用にあたっては直接メーカー様にご確認ください。​
            </Typography>
          </>
        );
      case 3:
        return (
          <>
            <Typography sx={styles.text}>
              この換算機能を有効にするため、まずは設定メニューから「CO2基準値」の確認・設定をして下さい。
              <br />
              初期値が入力されていますので、そのままご使用していただいても構いません。後で変更することや複数のCO2基準値を作ることも可能です。
            </Typography>
          </>
        );
      default:
        return 'Unknown step';
    }
  };

  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  return (
    <>
      <Modal open={show}>
        <Box sx={styles.modaleWrapper}>
          <Box
            sx={{
              backgroundColor: primary100,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              padding: activeStep === 0 ? '24px' : '16px'
            }}
          >
            <Grid
              container
              justifyContent={'center'}
              gap={activeStep === 0 ? '3px' : '0px'}
            >
              <Grid
                container
                flexDirection={'row'}
                gap={'4px'}
                justifyContent={'center'}
              >
                <img
                  src={logoImage}
                  style={{ width: '113px', height: 'auto' }}
                />
                <Typography sx={{ color: white }}>
                  {'[コムコス]'}
                  <Typography
                    component={'span'}
                    sx={{ display: activeStep === 0 ? 'inline' : 'none' }}
                  >
                    の
                  </Typography>
                </Typography>
              </Grid>
              <Typography
                sx={{
                  '@media screen and (max-width:600px)': {
                    fontSize: '20px'
                  },
                  color: white,
                  display: activeStep === 0 ? 'block' : 'none',
                  fontSize: '22px'
                }}
              >
                ご利用誠に有難うございます！
              </Typography>
            </Grid>
          </Box>
          <IconButton
            sx={{
              color: white,
              cursor: 'pointer',
              display: activeStep === 3 ? 'block' : 'none',
              p: 0,
              position: 'absolute',
              top: '17px',
              right: '24px'
            }}
            onClick={() => {
              setActiveStep(0);
              onClose();
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
            <Box sx={{ margin: '24px' }}>
              <Box key={activeStep} sx={{ width: '100%', mb: '36px' }}>
                {stepContent(activeStep)}
              </Box>
              <MobileStepper
                variant="dots"
                steps={4}
                position="static"
                activeStep={activeStep}
                sx={{
                  '.MuiMobileStepper-dot': { bgcolor: gray70 },
                  '.MuiMobileStepper-dotActive': { bgcolor: gray90 },
                  justifyContent: 'center',
                  mb: '24px'
                }}
                backButton={<></>}
                nextButton={<></>}
              />
              <Grid
                container
                flexDirection={'row'}
                gap={activeStep === 1 || activeStep === 2 ? '16px' : '0'}
                flexWrap={'nowrap'}
              >
                {/* 1枚目の時は非表示 */}
                <Grid item xs={activeStep === 0 ? 0 : 6}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{
                      display:
                        activeStep === 0 || activeStep === 3 ? 'none' : 'block',
                      width: '100%',
                      ...primaryButtonOutlinedColor
                    }}
                  >
                    戻る
                  </Button>
                </Grid>
                {/* 1枚目の時は幅いっぱいにする・4枚目の時は非表示 */}
                <Grid item xs={activeStep === 0 ? 12 : 6}>
                  <Button
                    variant="outlined"
                    onClick={handleNext}
                    sx={{
                      display: activeStep === 3 ? 'none' : 'block',
                      width: '100%',
                      ...primaryButtonBackgroundColor
                    }}
                  >
                    次へ
                  </Button>
                </Grid>
              </Grid>
              {activeStep === 3 && (
                <Grid container flexDirection={'column'} gap={'17px'}>
                  {userRole === userRoleEnum.leader ? (
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/standard')}
                        sx={{
                          width: '100%',
                          ...primaryButtonBackgroundColor
                        }}
                      >
                        CO2基準値を設定する
                      </Button>
                    </Grid>
                  ) : (
                    <></>
                  )}

                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/material')}
                      sx={{
                        width: '100%',
                        ...primaryButtonOutlinedColor
                      }}
                    >
                      まずは使ってみる！
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
export default Navipanel;
